import { hasPaintedFill, isClosedFillableGeometry } from "./fillPackGeometry";
import { extractViewBox, withMountedPathSvg } from "./svgUtils";

const FLATTEN_SKIP_TAGS = new Set([
    "defs",
    "clippath",
    "mask",
    "pattern",
    "symbol",
    "marker",
    "title",
    "desc",
    "metadata",
    "style",
    "script",
    "svg",
]);

function fmtCoord(n: number): string {
    return String(parseFloat(n.toFixed(4)));
}

function parsePointsList(s: string): { x: number; y: number }[] {
    const nums = s
        .trim()
        .split(/[\s,]+/)
        .map(parseFloat)
        .filter((n) => !Number.isNaN(n));
    const out: { x: number; y: number }[] = [];
    for (let i = 0; i + 1 < nums.length; i += 2) {
        out.push({ x: nums[i]!, y: nums[i + 1]! });
    }
    return out;
}

function localPointsForFlatten(el: SVGGeometryElement): { x: number; y: number }[] | null {
    if (el instanceof SVGRectElement) {
        const x = el.x.baseVal.value;
        const y = el.y.baseVal.value;
        const w = el.width.baseVal.value;
        const h = el.height.baseVal.value;
        if (!(w > 0 && h > 0)) return null;
        return [
            { x, y },
            { x: x + w, y },
            { x: x + w, y: y + h },
            { x, y: y + h },
        ];
    }
    if (el instanceof SVGCircleElement) {
        const cx = el.cx.baseVal.value;
        const cy = el.cy.baseVal.value;
        const r = el.r.baseVal.value;
        if (!(r > 0)) return null;
        return [
            { x: cx - r, y: cy - r },
            { x: cx + r, y: cy - r },
            { x: cx + r, y: cy + r },
            { x: cx - r, y: cy + r },
        ];
    }
    if (el instanceof SVGEllipseElement) {
        const cx = el.cx.baseVal.value;
        const cy = el.cy.baseVal.value;
        const rx = el.rx.baseVal.value;
        const ry = el.ry.baseVal.value;
        if (!(rx > 0 && ry > 0)) return null;
        return [
            { x: cx - rx, y: cy - ry },
            { x: cx + rx, y: cy - ry },
            { x: cx + rx, y: cy + ry },
            { x: cx - rx, y: cy + ry },
        ];
    }
    if (el instanceof SVGPolygonElement) {
        const pts = parsePointsList(el.getAttribute("points") ?? "");
        if (pts.length < 3) return null;
        return pts;
    }
    const bb = el.getBBox();
    if (!(bb.width > 0 && bb.height > 0)) return null;
    const { x, y, width: w, height: h } = bb;
    return [
        { x, y },
        { x: x + w, y },
        { x: x + w, y: y + h },
        { x, y: y + h },
    ];
}

function pathDFromPoints(pts: { x: number; y: number }[]): string {
    if (pts.length < 3) return "";
    let d = `M${fmtCoord(pts[0]!.x)} ${fmtCoord(pts[0]!.y)}`;
    for (let i = 1; i < pts.length; i++) {
        const p = pts[i]!;
        d += ` L${fmtCoord(p.x)} ${fmtCoord(p.y)}`;
    }
    d += " Z";
    return d;
}

function rootPoint(
    root: SVGSVGElement,
    el: SVGGraphicsElement,
    x: number,
    y: number,
): DOMPoint {
    const svgP = root.createSVGPoint();
    svgP.x = x;
    svgP.y = y;
    const ctm = el.getCTM();
    if (!ctm) return new DOMPoint(x, y);
    return svgP.matrixTransform(ctm);
}

function normalizedSolidFill(el: SVGGeometryElement): string | null {
    const cs = getComputedStyle(el);
    const fill = cs.fill;
    if (!fill || fill === "none" || fill === "transparent") return null;
    if (/^url\(/i.test(fill.trim())) return null;
    const m = /^rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*([\d.]+)\s*\)/.exec(
        fill,
    );
    if (m && Number.parseFloat(m[1]) === 0) return null;
    return fill.replace(/"/g, "&quot;");
}

function walkFlattenTargets(node: Element, out: SVGGeometryElement[]): void {
    const tag = node.tagName.toLowerCase();
    if (FLATTEN_SKIP_TAGS.has(tag)) {
        return;
    }
    if (node instanceof SVGGeometryElement) {
        if (isClosedFillableGeometry(node) && hasPaintedFill(node)) {
            out.push(node);
        }
    }
    for (let i = 0; i < node.children.length; i++) {
        walkFlattenTargets(node.children[i]!, out);
    }
}

/**
 * Bake ancestor transforms into root-space `<path>` elements with solid fills.
 * Keeps the root `viewBox` from the source markup when present; otherwise uses
 * the mounted root viewBox, or a tight bounds fallback if neither exists.
 * Skips nested `<svg>` subtrees, gradient/pattern fills, and open paths.
 */
export function flattenSvgToRootPaths(svgMarkup: string): string | null {
    const viewBoxFromMarkup = extractViewBox(svgMarkup)?.viewBox?.trim();
    return withMountedPathSvg(svgMarkup, (root) => {
        const targets: SVGGeometryElement[] = [];
        for (let i = 0; i < root.children.length; i++) {
            walkFlattenTargets(root.children[i]!, targets);
        }
        if (targets.length === 0) return null;

        const pathChunks: string[] = [];
        const needTightViewBox = !viewBoxFromMarkup;
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        const expandBounds = (pts: { x: number; y: number }[]) => {
            for (const p of pts) {
                minX = Math.min(minX, p.x);
                minY = Math.min(minY, p.y);
                maxX = Math.max(maxX, p.x);
                maxY = Math.max(maxY, p.y);
            }
        };

        for (const el of targets) {
            const fill = normalizedSolidFill(el);
            if (!fill) continue;

            const local = localPointsForFlatten(el);
            if (!local) continue;

            const worldPts = local.map((p) => {
                const wp = rootPoint(root, el, p.x, p.y);
                return { x: wp.x, y: wp.y };
            });

            if (needTightViewBox) {
                expandBounds(worldPts);
            }
            const d = pathDFromPoints(worldPts);
            if (!d) continue;
            pathChunks.push(`<path d="${d}" fill="${fill}"/>`);
        }

        if (pathChunks.length === 0) return null;

        let viewBoxAttr: string;
        if (viewBoxFromMarkup) {
            viewBoxAttr = viewBoxFromMarkup.replace(/"/g, "&quot;");
        } else {
            const b = root.viewBox.baseVal;
            if (b.width > 0 && b.height > 0) {
                viewBoxAttr = `${fmtCoord(b.x)} ${fmtCoord(b.y)} ${fmtCoord(b.width)} ${fmtCoord(b.height)}`;
            } else if (Number.isFinite(minX)) {
                viewBoxAttr = `${fmtCoord(minX)} ${fmtCoord(minY)} ${fmtCoord(maxX - minX)} ${fmtCoord(maxY - minY)}`;
            } else {
                return null;
            }
        }

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}" fill="none">\n${pathChunks.join("\n")}\n</svg>`;
    }) ?? null;
}

export function fileNameWithFlattenSuffix(name: string, flattened: boolean): string {
    if (!flattened) return name;
    if (name.toLowerCase().endsWith(".svg")) {
        return `${name.slice(0, -4)} (flattened).svg`;
    }
    return `${name} (flattened)`;
}
